import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { auth, db, onAuthStateChanged } from "@/firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { logDebug, logInfo, logError, logWarn } from "@/utils/secureLogger";

/**
 * Tutor status types based on the application and profile status
 */
export type TutorStatus = 
  | "pending" 
  | "active" 
  | "Active" 
  | "rejected" 
  | "document_issue" 
  | "approved" 
  | "resubmitted_under_review";

/**
 * Check if tutor can access features based on their status
 */
export function canTutorAccessFeatures(status: TutorStatus | null, profileStatus?: string): boolean {
  if (!status) return false;
  
  // Check both application status and profile status for tutors
  const allowedApplicationStatuses = ['active', 'Active', 'approved'];
  const hasValidApplicationStatus = allowedApplicationStatuses.includes(status);
  
  // Profile status must be 'approved' for tutors to access messaging
  const hasApprovedProfile = profileStatus === 'approved';
  
  return hasValidApplicationStatus && hasApprovedProfile;
}

/**
 * Get user-friendly status message for tutors
 */
export function getTutorStatusMessage(status: TutorStatus | null, profileStatus?: string): string {
  if (!status) return "Status unknown";
  
  // If profile is not approved, show profile-specific message
  if (profileStatus !== 'approved') {
    return "Your tutor profile is pending approval";
  }
  
  switch (status) {
    case "pending":
      return "Your application is being reviewed";
    case "active":
    case "Active":
      return "Your account is active";
    case "rejected":
      return "Your application was not approved";
    case "document_issue":
      return "Please update your documents";
    case "approved":
      return "Your application has been approved";
    case "resubmitted_under_review":
      return "Your resubmitted application is under review";
    default:
      return "Status unknown";
  }
}

/**
 * Vue composable for managing tutor status and access permissions
 * Provides reactive status state and real-time updates from Firestore
 */
export function useTutorStatus() {
  const status = ref<TutorStatus | null>(null);
  const profileStatus = ref<string | null>(null);
  const isLoading = ref(true);
  const error = ref<string | null>(null);
  let unsubscribe: (() => void) | null = null;

  // Computed properties for access control
  const canAccessFeatures = computed(() => {
    return canTutorAccessFeatures(status.value, profileStatus.value);
  });

  const canMessage = computed(() => canAccessFeatures.value);
  const canConductSessions = computed(() => canAccessFeatures.value);

  const statusMessage = computed(() => {
    return getTutorStatusMessage(status.value, profileStatus.value);
  });

  const isPending = computed(() => status.value === "pending");
  const isActive = computed(() => status.value === "active" || status.value === "Active");
  const isRejected = computed(() => status.value === "rejected");
  const isProfileApproved = computed(() => profileStatus.value === "approved");

  /**
   * Fetch tutor status from Firestore
   */
  const fetchStatus = async (userId?: string): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      // Get current user if userId not provided
      const currentUser = userId ? { uid: userId } : auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      logDebug("Fetching tutor status", { userId: currentUser.uid });

      // Fetch user document from Firestore
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      
      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      const userData = userDoc.data();
      // Check applicationStatus first, fallback to status for backward compatibility
      const userStatus = userData.applicationStatus || userData.status || "pending";
      const userProfileStatus = userData.profileStatus || "pending";

      logDebug("Raw tutor data from Firestore", { 
        userId: currentUser.uid,
        applicationStatus: userData.applicationStatus,
        status: userData.status,
        profileStatus: userData.profileStatus,
        resolvedStatus: userStatus,
        resolvedProfileStatus: userProfileStatus
      });

      // Validate application status
      if (!["pending", "active", "Active", "rejected", "document_issue", "approved", "resubmitted_under_review"].includes(userStatus)) {
        logWarn("Invalid application status value, defaulting to pending", { status: userStatus });
        status.value = "pending";
      } else {
        status.value = userStatus as TutorStatus;
      }

      // Set profile status
      profileStatus.value = userProfileStatus;

      logInfo("Tutor status fetched", { 
        userId: currentUser.uid, 
        applicationStatus: status.value,
        profileStatus: profileStatus.value,
        canMessage: canMessage.value
      });
    } catch (err) {
      logError("Error fetching tutor status", err);
      error.value = err instanceof Error ? err.message : "Failed to fetch status";
      status.value = "pending"; // Default to pending on error
      profileStatus.value = "pending";
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Set up real-time listener for status changes
   */
  const setupStatusListener = (userId?: string): void => {
    try {
      // Get current user if userId not provided
      const currentUser = userId ? { uid: userId } : auth.currentUser;
      if (!currentUser) {
        logWarn("Cannot setup status listener: no authenticated user");
        return;
      }

      logDebug("Setting up tutor status listener", { userId: currentUser.uid });

      // Set up Firestore real-time listener
      unsubscribe = onSnapshot(
        doc(db, "users", currentUser.uid),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            const userStatus = userData.applicationStatus || userData.status || "pending";
            const userProfileStatus = userData.profileStatus || "pending";

            logDebug("Tutor status updated via listener", { 
              userId: currentUser.uid,
              applicationStatus: userData.applicationStatus,
              status: userData.status,
              profileStatus: userData.profileStatus,
              resolvedStatus: userStatus,
              resolvedProfileStatus: userProfileStatus
            });

            // Validate and set application status
            if (!["pending", "active", "Active", "rejected", "document_issue", "approved", "resubmitted_under_review"].includes(userStatus)) {
              logWarn("Invalid application status value from listener, defaulting to pending", { status: userStatus });
              status.value = "pending";
            } else {
              status.value = userStatus as TutorStatus;
            }

            // Set profile status
            profileStatus.value = userProfileStatus;

            logInfo("Tutor status updated", { 
              userId: currentUser.uid, 
              applicationStatus: status.value,
              profileStatus: profileStatus.value,
              canMessage: canMessage.value
            });
          } else {
            logWarn("Tutor document does not exist");
            status.value = "pending";
            profileStatus.value = "pending";
          }
          isLoading.value = false;
        },
        (err) => {
          logError("Error in tutor status listener", err);
          error.value = "Failed to listen for status updates";
          isLoading.value = false;
        }
      );
    } catch (err) {
      logError("Error setting up tutor status listener", err);
      error.value = "Failed to setup status listener";
    }
  };

  /**
   * Clean up the real-time listener
   */
  const cleanup = (): void => {
    if (unsubscribe) {
      logDebug("Cleaning up tutor status listener");
      unsubscribe();
      unsubscribe = null;
    }
  };

  // Set up listener on mount, but wait for authentication
  onMounted(() => {
    // Listen for auth state changes and set up listener when user is authenticated
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        logDebug("User authenticated, setting up tutor status listener", { userId: user.uid });
        setupStatusListener();
      } else {
        logDebug("User not authenticated, cleaning up tutor status listener");
        cleanup();
        status.value = null;
        profileStatus.value = null;
        isLoading.value = false;
      }
    });

    // Clean up auth listener on unmount
    onBeforeUnmount(() => {
      unsubscribeAuth();
      cleanup();
    });
  });

  return {
    // State
    status,
    profileStatus,
    isLoading,
    error,
    
    // Computed properties
    canAccessFeatures,
    canMessage,
    canConductSessions,
    statusMessage,
    isPending,
    isActive,
    isRejected,
    isProfileApproved,
    
    // Methods
    fetchStatus,
    setupStatusListener,
    cleanup,
  };
}
